import { useMemo } from 'react';
import { Slider as AriaSlider, SliderThumb, SliderTrack } from 'react-aria-components';
import styled from 'styled-components';
import { colors } from '@karnott/colors';
import { msDuration, pixelSize, pixelSpacing, spacing } from '@karnott/theme';

const Container = styled.div`
  --thumb-size: 16px;
  width: 100%;

  .react-aria-SliderTrack {
    flex: 1;
    position: relative;
    height: ${pixelSpacing('xSmall')};
    border-radius: ${spacing('xSmall') / 2}px;
    background-color: ${colors('grey', 200)};
  }

  .react-aria-SliderThumb {
    top: 50%;
    height: var(--thumb-size);
    width: var(--thumb-size);
    background-color: ${colors('green')};
    border-radius: 50%;
    box-shadow:
      0 3px 6px rgba(0, 0, 0, 0.06),
      0 3px 6px rgba(0, 0, 0, 0.13);
    transition: background-color ${msDuration('short')} ease-in-out;

    &:hover,
    &:focus-within {
      background-color: ${colors('green', 'dark')};
    }
  }
`;

const ThumbLabel = styled.p`
  margin: 0;
  font-size: ${pixelSize('small')};
  color: ${colors('black', 400)};
  white-space: nowrap;
  width: max-content;
  margin-left: 50%;
  translate: -50%;
  margin-top: calc(1.2 * var(--thumb-size));
  background-color: ${colors('white')};
`;

const ActiveTrack = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: ${spacing('xSmall') / 2}px;
  background-color: ${colors('green')};
`;

export type SliderValue = {
  start?: number;
  end: number;
};

type SliderEvent = (value: SliderValue) => void;

type Props = {
  /** The minimum possible value */
  min?: number;
  /** The maximum possible value */
  max?: number;
  /**
   * The value of the slider. If the `start` property is not set, then the slider selects only one value, instead of a
   * range
   */
  value: SliderValue;
  /** Callback when the value changes */
  onChange?: SliderEvent;
  /** Callback when the dragging motion has ended */
  onDragEnd?: SliderEvent;
  /** Function to display the values below the slider thumbs. If omitted, the values are not displayed */
  format?: (value: number) => string;
};

/** A slider to choose a numeric value, or a numeric range */
export function Slider({ min = 0, max = 100, value, onChange, onDragEnd, format }: Props) {
  const isMultiple = useMemo(() => {
    return value && value.start !== undefined && value.start !== null;
  }, [value]);

  return (
    <Container>
      <AriaSlider
        minValue={min}
        maxValue={max}
        value={isMultiple ? [value.start!, value.end] : value.end}
        onChange={(value) =>
          Array.isArray(value) ? onChange?.({ start: value[0], end: value[1]! }) : onChange?.({ end: value })
        }
        onChangeEnd={(value) =>
          Array.isArray(value) ? onDragEnd?.({ start: value[0], end: value[1]! }) : onDragEnd?.({ end: value })
        }
        aria-label="Slider"
      >
        <SliderTrack>
          {({ state }) => (
            <>
              <ActiveTrack
                style={{
                  width: isMultiple
                    ? (state.getThumbPercent(1) - state.getThumbPercent(0)) * 100 + '%'
                    : state.getThumbPercent(0) * 100 + '%',
                  left: isMultiple ? state.getThumbPercent(0) * 100 + '%' : 0,
                }}
              />

              {state.values.map((value, i) => (
                <SliderThumb key={i} index={i} data-testid={`sdk-slider-thumb-${i}`}>
                  {format ? <ThumbLabel>{format(value)} </ThumbLabel> : null}
                </SliderThumb>
              ))}
            </>
          )}
        </SliderTrack>
      </AriaSlider>
    </Container>
  );
}
